import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import {
  startWith,
  debounceTime,
  distinctUntilChanged,
  map,
} from 'rxjs/operators';

type UserOrTeam = 'user' | 'team';
@Component({
  selector: 'zint-permissions',
  templateUrl: './zint-permissions.component.html',
  styleUrl: './zint-permissions.component.css',
  standalone: false,
})
export class ZintPermissionsComponent {
  @Input() allUsers: Record<string, any>[] = [];
  @Input() allTeams: Record<string, any>[] = [];
  @Input() existingTeams: Record<string, any>[] = [];
  @Input() existingUsers: Record<string, any>[] = [];
  @Input() usersLabel = 'Individuals';
  @Input() teamsLabel = 'Teams';
  @Input() permissionLabel = '';

  @Output() onPermissionChange = new EventEmitter();

  // search inputs
  userSearchControl = new FormControl('');
  teamSearchControl = new FormControl('');

  // Observables for filtered lists
  filteredUsers$: Observable<Record<string, any>[]>;
  filteredTeams$: Observable<Record<string, any>[]>;

  // state for dropdown visibility
  userFieldFocused: boolean = false;
  teamFieldFocused: boolean = false;

  // selected items lists
  selectedUsers: Record<string, any>[] = [];
  selectedTeams: Record<string, any>[] = [];

  ngOnInit(): void {
    // Setup user search observable
    this.filteredUsers$ = this.userSearchControl.valueChanges.pipe(
      startWith(this.userSearchControl.value || ''),
      debounceTime(220),
      distinctUntilChanged(),
      map(searchTerm =>
        this.filterUsersOrTeams(searchTerm.trim() || '', 'user')
      )
    );

    // Setup team search observable
    this.filteredTeams$ = this.teamSearchControl.valueChanges.pipe(
      startWith(this.teamSearchControl.value || ''),
      debounceTime(220),
      distinctUntilChanged(),
      map(searchTerm =>
        this.filterUsersOrTeams(searchTerm.trim() || '', 'team')
      )
    );

    // show existing selections
    this.handleExistingPermissions();
    this.updateSelectedUsersOrTeamsList();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['allUsers']) {
      this.updateSelectedUsersOrTeamsList('user');
    }
    if (changes['allTeams']) {
      this.updateSelectedUsersOrTeamsList('team');
    }
  }

  filterUsersOrTeams(
    searchTerm: string,
    group: UserOrTeam
  ): Record<string, any>[] {
    const lowerCaseSearchTerm = searchTerm.toLowerCase();

    if (group === 'user') {
      return this.allUsers?.filter(user =>
        user.email.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
    if (group === 'team') {
      return this.allTeams?.filter(team =>
        team.name.toLowerCase().includes(lowerCaseSearchTerm)
      );
    }
  }

  onSelectionChange({
    userOrTeamObj,
    isChecked,
    group,
  }: {
    userOrTeamObj: Record<string, any>;
    isChecked: boolean;
    group: UserOrTeam;
  }): void {
    let foundObj = null;

    if (group === 'user') {
      foundObj = this.allUsers.find(u => u.userId === userOrTeamObj.userId);
    } else if (group === 'team') {
      foundObj = this.allTeams.find(t => t.id === userOrTeamObj.id);
    }

    if (foundObj) {
      foundObj.selected = isChecked;

      this.updateSelectedUsersOrTeamsList(group);

      this.onPermissionChange.emit(foundObj);
    }
  }

  removeSelectedUserOrTeam(
    objToRemove: Record<string, any>,
    group: UserOrTeam
  ): void {
    let foundObj = null;

    if (group === 'user') {
      foundObj = this.allUsers.find(u => u.userId === objToRemove.userId);
    } else if (group === 'team') {
      foundObj = this.allTeams.find(t => t.id === objToRemove.id);
    }

    if (foundObj) {
      foundObj.selected = false;
      this.updateSelectedUsersOrTeamsList(group);

      this.onPermissionChange.emit(foundObj);
    }
  }

  handleExistingPermissions(): void {
    if (!this.existingTeams?.length && !this.existingUsers?.length) return;

    this.allTeams = this.allTeams.map(team => ({
      ...team,
      selected: this.existingPermissionShouldBeSelected({
        group: 'team',
        searchKey: team.name,
      }),
    }));

    this.allUsers = this.allUsers.map(user => ({
      ...user,
      selected: this.existingPermissionShouldBeSelected({
        group: 'user',
        searchKey: user.email,
      }),
    }));
  }

  existingPermissionShouldBeSelected({
    group,
    searchKey,
  }: {
    group: UserOrTeam;
    searchKey?: string;
  }): boolean {
    let foundExistingObj: Record<string, any> = null;

    foundExistingObj =
      group === 'team'
        ? this.existingTeams.find(team => team.name === searchKey)
        : this.existingUsers.find(user => user.email === searchKey);

    return !!foundExistingObj;
  }

  updateSelectedUsersOrTeamsList(group?: UserOrTeam): void {
    // if !group, update both
    if (!group || group === 'user') {
      this.selectedUsers = this.allUsers?.filter(user => user.selected);
    }

    if (!group || group === 'team') {
      this.selectedTeams = this.allTeams?.filter(team => team.selected);
    }
  }
}
